import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Checkbox, ListItemText } from '@mui/material';
import _ from 'lodash';

// eslint-disable-next-line functional/no-mixed-type
interface SelectBoxProps {
  items: string[];
  values: string[] | string;
  label?: string;
  inputLabel?: string;
  onFilterChange: (filterType: string, filterValue: string | string[]) => void;
  type: string;
  disabled?: boolean;
  withAllOption?: boolean;
  allOptionLabel?: string;
  inputId?: string;
  multiple?: boolean;
  disabledItems?: string[];
}

const SelectBox: React.FC<SelectBoxProps> = ({
  items,
  values,
  label,
  inputLabel,
  onFilterChange,
  type,
  disabled,
  withAllOption,
  allOptionLabel,
  inputId,
  multiple,
  disabledItems
}) => {
  const handleChange = (event: SelectChangeEvent<typeof values>) => {
    const {
      target: { value }
    } = event;
    let selectedValues: string[] | string;

    if (multiple) {
      selectedValues = typeof value === 'string' ? value.split(',') : value;

      if (withAllOption) {
        if (selectedValues.includes('All')) {
          if ((values as string[]).includes('All')) {
            selectedValues = selectedValues.filter((value) => value !== 'All'); // Deselect "All" if it was already selected
          } else {
            selectedValues = ['All']; // Select "All" option
          }
        }
      }
    } else {
      selectedValues = value as string;
    }

    onFilterChange(type, selectedValues);
  };

  const sortedUniqueItems = _.uniq(_.pull(items, '')).sort((a, b) => a?.toLowerCase().localeCompare(b.toLowerCase()));

  return (
    <FormControl sx={{ minWidth: 160, maxWidth: 400 }}>
      <InputLabel id='select-label'>{inputLabel}</InputLabel>
      <Select
        labelId='select-label'
        label={label}
        value={values}
        onChange={handleChange}
        displayEmpty
        disabled={disabled}
        sx={{ backgroundColor: disabled ? '#f5f5f5' : '#ffff' }}
        inputProps={{ 'data-testid': inputId }}
        multiple={multiple}
        renderValue={(selected) => {
          if (multiple) {
            if ((selected as string[]).includes('All')) {
              return allOptionLabel || 'All';
            }
            return (selected as string[]).join(', ');
          }
          if (selected === 'All') {
            return allOptionLabel || 'All';
          }
          return selected as string;
        }}
      >
        {withAllOption && (
          <MenuItem value={'All'}>
            {multiple && <Checkbox checked={(values as string[]).includes('All')} />}
            <ListItemText primary={allOptionLabel || 'All'} />
          </MenuItem>
        )}
        {sortedUniqueItems.map((item) => (
          <MenuItem disabled={disabledItems?.includes(item) || false} key={item} value={item}>
            {multiple && <Checkbox checked={(values as string[]).includes(item)} />}
            <ListItemText primary={item} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SelectBox;
